// src/pages/OrusLanding.tsx
// ORUS Dev — Landing Page parceiro da Federação
// ✅ Sem header próprio (usa o da federação)
// ✅ Tom âmbar/dourado alinhado à federação
// ✅ Seção de vídeo e marketing digital
// ✅ WhatsApp 55 98 97019-0739

import { useEffect, useRef, useState } from "react";

const WA_NUMBER = "5598970190739";
const WA_MSG = encodeURIComponent(
  "Vim pela Federação e quero construir minha identidade digital."
);
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${WA_MSG}`;

// ── WhatsApp SVG ──────────────────────────────────────────────────────────────
const WAIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

// ── Reveal hook ───────────────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setV(true); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, v };
}

const Reveal = ({ children, delay = 0, y = 28 }: { children: React.ReactNode; delay?: number; y?: number }) => {
  const { ref, v } = useReveal();
  return (
    <div ref={ref} style={{
      opacity: v ? 1 : 0,
      transform: v ? 'none' : `translateY(${y}px)`,
      transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
};

// ── Marquee ───────────────────────────────────────────────────────────────────
const MARQUEE = [
  'Sites Institucionais', 'Identidade Visual', 'Vídeos Profissionais',
  'Marketing Digital', 'Apps Mobile', 'Gestão de Redes Sociais',
  'Motion Design', 'Sistemas sob Medida',
];

// ─────────────────────────────────────────────────────────────────────────────
export default function OrusLanding() {
  const [waHover, setWaHover] = useState(false);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        html{scroll-behavior:smooth}

        .orus-bg { background:#0a0a0a; }

        @keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes pulse-gold { 0%,100%{opacity:.6} 50%{opacity:1} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }

        .gold { color:#C8A96E; }
        .gold-border { border-color:rgba(200,169,110,0.22); }
        .card-hover { transition:background .35s,transform .35s,box-shadow .35s; }
        .card-hover:hover { background:#1c1c1c !important; transform:translateY(-4px); box-shadow:0 20px 60px rgba(200,169,110,0.12); }
        .btn-gold { position:relative; overflow:hidden; transition:all .3s; }
        .btn-gold::after { content:''; position:absolute; inset:0; background:rgba(255,255,255,0.08); opacity:0; transition:opacity .3s; }
        .btn-gold:hover::after { opacity:1; }
        .btn-gold:hover { box-shadow:0 12px 40px rgba(200,169,110,0.35); transform:translateY(-2px); }
        .wa-btn { transition:all .3s; }
        .wa-btn:hover { box-shadow:0 8px 30px rgba(37,211,102,0.4); transform:translateY(-2px); }

        @media(max-width:768px){
          .hero-title{font-size:clamp(38px,10vw,64px)!important}
          .services-grid{grid-template-columns:1fr!important}
          .process-grid{grid-template-columns:1fr 1fr!important}
          .about-grid{grid-template-columns:1fr!important; gap:40px!important}
          .video-grid{grid-template-columns:1fr!important}
        }
      `}</style>

      <div className="orus-bg" style={{ fontFamily:"'Outfit',sans-serif", fontWeight:300, color:'#F0EAE0', overflowX:'hidden' }}>

        {/* ══ HERO ══════════════════════════════════════════════════════════ */}
        <section style={{
          minHeight:'100vh', display:'flex', flexDirection:'column',
          justifyContent:'flex-end', padding:'0 5% 80px',
          position:'relative', overflow:'hidden',
          // Afasta do header da federação
          paddingTop: '120px',
        }}>
          {/* Glow âmbar */}
          <div style={{ position:'absolute', width:700, height:700, background:'radial-gradient(circle, rgba(200,169,110,0.13) 0%, transparent 70%)', top:-80, right:-100, borderRadius:'50%', pointerEvents:'none' }}/>
          <div style={{ position:'absolute', width:400, height:400, background:'radial-gradient(circle, rgba(200,169,110,0.07) 0%, transparent 70%)', bottom:0, left:-60, borderRadius:'50%', pointerEvents:'none' }}/>

          {/* Linha vertical decorativa */}
          <div style={{ position:'absolute', left:'5%', top:0, bottom:0, width:1, background:'linear-gradient(to bottom, transparent, rgba(200,169,110,0.35), transparent)', pointerEvents:'none' }}/>

          <div style={{
            opacity:0, transform:'translateY(20px)',
            animation:'float 0s ease forwards',
            animationDelay:'0.1s', animationFillMode:'forwards',
          }}>
            <p style={{ fontSize:11, letterSpacing:'0.3em', textTransform:'uppercase', color:'#C8A96E', marginBottom:24 }}>
              Parceiro Oficial · Federação dos Profissionais da Beleza
            </p>
          </div>

          <h1 className="hero-title" style={{
            fontFamily:"'Cormorant Garamond',serif",
            fontSize:'clamp(50px,8vw,120px)',
            fontWeight:300, lineHeight:0.9,
            letterSpacing:'-0.02em', maxWidth:900,
          }}>
            Sua identidade<br/>
            <em style={{ fontStyle:'italic', color:'#C8A96E' }}>digital</em> no nível<br/>
            que você merece.
          </h1>

          <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginTop:56, flexWrap:'wrap', gap:32 }}>
            <p style={{ maxWidth:380, fontSize:15, lineHeight:1.85, color:'#7a7060', borderLeft:'1px solid rgba(200,169,110,0.2)', paddingLeft:20 }}>
              Criamos sites, aplicativos, sistemas, vídeos e estratégias de marketing para profissionais e escolas que exigem excelência em cada detalhe.
            </p>
            <div style={{ display:'flex', gap:32 }}>
              {[['Sites','& Apps'],['Vídeos','& Motion'],['Marketing','& Estratégia']].map(([n,l]) => (
                <div key={n} style={{ textAlign:'right' }}>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:13, fontWeight:300, color:'#C8A96E', lineHeight:1 }}>{n}</div>
                  <div style={{ fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:'#555040', marginTop:4 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:'flex', gap:16, marginTop:48, flexWrap:'wrap' }}>
            <a href={WA_LINK} target="_blank" rel="noreferrer" className="btn-gold" style={{
              display:'inline-flex', alignItems:'center', gap:12,
              background:'linear-gradient(135deg,#C8A96E,#a8854e)',
              color:'#0a0a0a', padding:'16px 36px',
              fontSize:13, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase',
              textDecoration:'none', borderRadius:4,
            }}>
              <WAIcon size={18}/> Falar no WhatsApp
            </a>
            <a href="#servicos" style={{
              display:'inline-flex', alignItems:'center', gap:10,
              border:'1px solid rgba(200,169,110,0.25)', color:'#C8A96E',
              padding:'16px 36px', fontSize:13, letterSpacing:'0.1em',
              textTransform:'uppercase', textDecoration:'none', borderRadius:4,
              transition:'border-color .3s, background .3s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background='rgba(200,169,110,0.08)'; (e.currentTarget as HTMLElement).style.borderColor='#C8A96E'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background='transparent'; (e.currentTarget as HTMLElement).style.borderColor='rgba(200,169,110,0.25)'; }}
            >
              Ver Serviços
            </a>
          </div>
        </section>

        {/* ══ MARQUEE ══ */}
        <div style={{ borderTop:'1px solid rgba(200,169,110,0.15)', borderBottom:'1px solid rgba(200,169,110,0.15)', overflow:'hidden', padding:'16px 0', background:'#111' }}>
          <div style={{ display:'flex', whiteSpace:'nowrap', animation:'marquee 22s linear infinite' }}>
            {[...MARQUEE,...MARQUEE].map((item, i) => (
              <span key={i} style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontStyle:'italic', color:'#7a7060', padding:'0 36px', display:'inline-flex', alignItems:'center', gap:36 }}>
                {item}<span style={{ fontSize:7, color:'#C8A96E' }}>◆</span>
              </span>
            ))}
          </div>
        </div>

        {/* ══ SERVIÇOS DIGITAIS ══════════════════════════════════════════════ */}
        <section id="servicos" style={{ padding:'110px 5%' }}>
          <Reveal>
            <p style={{ fontSize:10, letterSpacing:'0.32em', textTransform:'uppercase', color:'#C8A96E', marginBottom:14, display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ display:'block', width:28, height:1, background:'#C8A96E' }}/>Serviços Digitais
            </p>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(34px,5vw,60px)', fontWeight:300, lineHeight:1.1, color:'#F0EAE0', marginBottom:64 }}>
              Tudo que sua marca<br/><em style={{ color:'#C8A96E' }}>precisa</em> para crescer
            </h2>
          </Reveal>
          <div className="services-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:1, border:'1px solid rgba(200,169,110,0.15)', background:'rgba(200,169,110,0.15)' }}>
            {[
              { num:'01', sym:'◈', title:'Sites & Landing Pages', desc:'Presença digital elegante para sua escola, clínica ou federação — com design exclusivo e otimização para conversão.', tags:['Design Premium','SEO','Responsivo'] },
              { num:'02', sym:'◉', title:'Aplicativos & Sistemas', desc:'Portais de associados, sistemas de matrículas, emissão de certificados digitais e automações para otimizar sua operação.', tags:['React Native','PWA','API REST'] },
              { num:'03', sym:'◇', title:'Identidade Visual', desc:'Criação ou revitalização completa da sua marca — logo, paleta, tipografia, manual de identidade e aplicações.', tags:['Branding','Logo','Manual'] },
            ].map((s,i) => (
              <Reveal key={s.num} delay={i*100}>
                <div className="card-hover" style={{ background:'#161616', padding:'44px 36px', position:'relative', overflow:'hidden' }}>
                  <p style={{ fontSize:10, letterSpacing:'0.2em', color:'#C8A96E', marginBottom:28, opacity:.7 }}>{s.num}</p>
                  <p style={{ fontSize:26, marginBottom:20, color:'#C8A96E' }}>{s.sym}</p>
                  <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, marginBottom:14, lineHeight:1.2, color:'#F0EAE0' }}>{s.title}</h3>
                  <p style={{ fontSize:13.5, lineHeight:1.85, color:'#7a7060' }}>{s.desc}</p>
                  <div style={{ marginTop:24, display:'flex', flexWrap:'wrap', gap:8 }}>
                    {s.tags.map(t => (
                      <span key={t} style={{ fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'#C8A96E', border:'1px solid rgba(200,169,110,0.2)', padding:'5px 12px' }}>{t}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ══ VÍDEO & MARKETING (seção nova) ════════════════════════════════ */}
        <section style={{ padding:'0 5% 110px', background:'#0a0a0a' }}>
          <div style={{ borderTop:'1px solid rgba(200,169,110,0.15)', paddingTop:80 }}>
            <Reveal>
              <p style={{ fontSize:10, letterSpacing:'0.32em', textTransform:'uppercase', color:'#C8A96E', marginBottom:14, display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ display:'block', width:28, height:1, background:'#C8A96E' }}/>Vídeo & Marketing
              </p>
              <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(34px,5vw,60px)', fontWeight:300, lineHeight:1.1, color:'#F0EAE0', marginBottom:20 }}>
                Uma agência<br/><em style={{ color:'#C8A96E' }}>completa</em> ao seu lado
              </h2>
              <p style={{ fontSize:15, lineHeight:1.85, color:'#7a7060', maxWidth:520, marginBottom:64 }}>
                Além de tecnologia, produzimos conteúdo que vende. Motion design, vídeos institucionais, reels, gestão de redes sociais e campanhas que constroem autoridade no mercado da beleza.
              </p>
            </Reveal>

            <div className="video-grid" style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:1, border:'1px solid rgba(200,169,110,0.15)', background:'rgba(200,169,110,0.15)', marginBottom:64 }}>
              {[
                {
                  sym:'▶', title:'Vídeos Institucionais',
                  desc:'Apresentações profissionais da sua escola, cursos ou serviços — para atrair alunos e parceiros com credibilidade.',
                  tags:['Roteiro','Gravação','Edição'],
                },
                {
                  sym:'◎', title:'Motion Design & Reels',
                  desc:'Animações e vídeos curtos que capturam atenção no Instagram, TikTok e WhatsApp — feitos para converter.',
                  tags:['After Effects','Reels','Stories'],
                },
                {
                  sym:'◐', title:'Gestão de Redes Sociais',
                  desc:'Planejamento editorial, criação de posts, legendas e estratégia de conteúdo para crescer com consistência.',
                  tags:['Instagram','Feed','Estratégia'],
                },
                {
                  sym:'◑', title:'Campanhas & Tráfego Pago',
                  desc:'Google Ads, Meta Ads e remarketing para atrair alunos, filiados e clientes para sua escola ou serviço.',
                  tags:['Google Ads','Meta Ads','ROI'],
                },
              ].map((s, i) => (
                <Reveal key={s.title} delay={i*80}>
                  <div className="card-hover" style={{ background:'#161616', padding:'40px 34px', position:'relative', overflow:'hidden' }}>
                    <p style={{ fontSize:28, marginBottom:18, color:'#C8A96E' }}>{s.sym}</p>
                    <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:400, marginBottom:12, lineHeight:1.2, color:'#F0EAE0' }}>{s.title}</h3>
                    <p style={{ fontSize:13.5, lineHeight:1.85, color:'#7a7060' }}>{s.desc}</p>
                    <div style={{ marginTop:20, display:'flex', flexWrap:'wrap', gap:8 }}>
                      {s.tags.map(t => (
                        <span key={t} style={{ fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'#C8A96E', border:'1px solid rgba(200,169,110,0.2)', padding:'5px 12px' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Destaque central */}
            <Reveal delay={200}>
              <div style={{ background:'linear-gradient(135deg, rgba(200,169,110,0.08), rgba(200,169,110,0.03))', border:'1px solid rgba(200,169,110,0.18)', borderRadius:4, padding:'52px 48px', textAlign:'center', maxWidth:760, margin:'0 auto' }}>
                <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(22px,3.5vw,36px)', fontWeight:300, fontStyle:'italic', color:'#F0EAE0', lineHeight:1.4, marginBottom:32 }}>
                  "Da identidade ao conteúdo, do sistema à campanha — entregamos o ecossistema completo para sua marca crescer no digital."
                </p>
                <a href={WA_LINK} target="_blank" rel="noreferrer" className="btn-gold" style={{
                  display:'inline-flex', alignItems:'center', gap:10,
                  background:'linear-gradient(135deg,#C8A96E,#a8854e)',
                  color:'#0a0a0a', padding:'14px 32px',
                  fontSize:12, fontWeight:600, letterSpacing:'0.1em',
                  textTransform:'uppercase', textDecoration:'none', borderRadius:4,
                }}>
                  <WAIcon size={16}/> Quero uma proposta
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ══ COMO TRABALHAMOS ══ */}
        <section style={{ padding:'110px 5%', background:'#111' }}>
          <Reveal>
            <p style={{ fontSize:10, letterSpacing:'0.32em', textTransform:'uppercase', color:'#C8A96E', marginBottom:14, display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ display:'block', width:28, height:1, background:'#C8A96E' }}/>Como funciona
            </p>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(34px,5vw,60px)', fontWeight:300, lineHeight:1.1, color:'#F0EAE0', marginBottom:64 }}>
              Do conceito ao<br/><em style={{ color:'#C8A96E' }}>produto final</em>
            </h2>
          </Reveal>
          <div className="process-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', borderTop:'1px solid rgba(200,169,110,0.15)' }}>
            {[
              { n:'01', title:'Diagnóstico',    desc:'Entendemos sua realidade, objetivos e o que você precisa resolver com tecnologia ou marketing.' },
              { n:'02', title:'Proposta',        desc:'Escopo claro com prazo, investimento e entregáveis definidos. Sem surpresas.' },
              { n:'03', title:'Produção',        desc:'Desenvolvemos com revisões periódicas. Você aprova cada etapa.' },
              { n:'04', title:'Entrega & Suporte', desc:'Lançamos, treinamos sua equipe e continuamos disponíveis para crescer junto.' },
            ].map((step, i) => (
              <Reveal key={step.n} delay={i*100}>
                <div style={{ padding:'44px 32px', borderRight: i < 3 ? '1px solid rgba(200,169,110,0.15)' : 'none' }}>
                  <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:52, fontWeight:300, color:'rgba(200,169,110,0.12)', lineHeight:1, marginBottom:24 }}>{step.n}</p>
                  <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, marginBottom:12, color:'#F0EAE0' }}>{step.title}</h4>
                  <p style={{ fontSize:13, color:'#7a7060', lineHeight:1.8 }}>{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ══ POR QUE ORUS ══ */}
        <section style={{ padding:'110px 5%', background:'#0a0a0a' }}>
          <div className="about-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:80, alignItems:'center' }}>
            <Reveal>
              <div style={{ border:'1px solid rgba(200,169,110,0.2)', padding:'52px 44px', background:'#161616', position:'relative' }}>
                <div style={{ position:'absolute', inset:-6, border:'1px solid rgba(200,169,110,0.1)', pointerEvents:'none' }}/>
                <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontStyle:'italic', fontWeight:300, lineHeight:1.5, color:'#F0EAE0' }}>
                  "Tecnologia e criatividade a serviço do propósito — somos o parceiro que entende o mercado para que você cresça."
                </p>
                <p style={{ marginTop:28, fontSize:10, letterSpacing:'0.2em', textTransform:'uppercase', color:'#C8A96E' }}>— Equipe ORUS Dev</p>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <p style={{ fontSize:10, letterSpacing:'0.32em', textTransform:'uppercase', color:'#C8A96E', marginBottom:14, display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ display:'block', width:28, height:1, background:'#C8A96E' }}/>Por que escolher
              </p>
              <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(30px,4vw,50px)', fontWeight:300, lineHeight:1.15, color:'#F0EAE0', marginBottom:32 }}>
                Especialistas no<br/><em style={{ color:'#C8A96E' }}>ecossistema</em> da beleza
              </h2>
              <div style={{ borderTop:'1px solid rgba(200,169,110,0.15)' }}>
                {[
                  'Parceiro oficial da Federação',
                  'Atendimento personalizado do início ao fim',
                  'Design exclusivo — sem templates genéricos',
                  'Produção de vídeo e marketing integrados',
                  'Entrega pontual com metodologia ágil',
                  'Suporte e manutenção contínuos',
                ].map(f => (
                  <div key={f} style={{ padding:'18px 0', borderBottom:'1px solid rgba(200,169,110,0.1)', display:'flex', alignItems:'center', gap:14, fontSize:14, color:'#D0C8B8', cursor:'default' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color='#C8A96E'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color='#D0C8B8'; }}
                  >
                    <span style={{ color:'#C8A96E', flexShrink:0, fontSize:12 }}>—</span>{f}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ══ CTA FINAL ══ */}
        <section style={{ textAlign:'center', position:'relative', overflow:'hidden', padding:'130px 5%', background:'#111' }}>
          <div style={{ position:'absolute', width:600, height:600, background:'radial-gradient(circle, rgba(200,169,110,0.1) 0%, transparent 65%)', top:'50%', left:'50%', transform:'translate(-50%,-50%)', pointerEvents:'none' }}/>
          <Reveal>
            <p style={{ fontSize:10, letterSpacing:'0.35em', textTransform:'uppercase', color:'#C8A96E', marginBottom:20 }}>Próximo passo</p>
          </Reveal>
          <Reveal delay={100}>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(40px,7vw,88px)', fontWeight:300, lineHeight:0.95, marginBottom:48, color:'#F0EAE0' }}>
              Vamos construir sua<br/><em style={{ fontStyle:'italic', color:'#C8A96E' }}>identidade digital</em>?
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16, flexWrap:'wrap' }}>
              <a href={WA_LINK} target="_blank" rel="noreferrer" className="btn-gold" style={{
                display:'inline-flex', alignItems:'center', gap:12,
                background:'linear-gradient(135deg,#C8A96E,#a8854e)',
                color:'#0a0a0a', padding:'18px 40px',
                fontSize:13, fontWeight:600, letterSpacing:'0.1em',
                textTransform:'uppercase', textDecoration:'none', borderRadius:4,
              }}>
                <WAIcon size={18}/> Falar no WhatsApp
              </a>
              <a href="mailto:contato@orusdev.com.br" style={{
                display:'inline-flex', alignItems:'center', gap:12,
                border:'1px solid rgba(200,169,110,0.22)', color:'#C8A96E',
                padding:'18px 40px', fontSize:13, letterSpacing:'0.1em',
                textTransform:'uppercase', textDecoration:'none', borderRadius:4,
                transition:'all .3s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor='#C8A96E'; (e.currentTarget as HTMLElement).style.background='rgba(200,169,110,0.08)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor='rgba(200,169,110,0.22)'; (e.currentTarget as HTMLElement).style.background='transparent'; }}
              >
                Enviar por E-mail
              </a>
            </div>
          </Reveal>
        </section>

        {/* ══ FOOTER ══ */}
        <footer style={{ borderTop:'1px solid rgba(200,169,110,0.15)', padding:'40px 5%', display:'flex', alignItems:'center', justifyContent:'space-between', background:'#111', flexWrap:'wrap', gap:16 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:600, letterSpacing:'0.12em', color:'#C8A96E', textTransform:'uppercase' }}>
            ORUS<span style={{ color:'#F0EAE0' }}> Dev</span>
          </div>
          <p style={{ fontSize:11, color:'#555040', letterSpacing:'0.08em', textAlign:'center' }}>
            Parceiro oficial do ecossistema digital da Federação dos Profissionais da Beleza
          </p>
          <p style={{ fontSize:11, color:'#555040', letterSpacing:'0.08em' }}>© 2026 · Todos os direitos reservados</p>
        </footer>

        {/* ══ WHATSAPP FLUTUANTE ══ */}
        <div style={{ position:'fixed', bottom:32, right:32, zIndex:500, display:'flex', alignItems:'center' }}
          onMouseEnter={() => setWaHover(true)}
          onMouseLeave={() => setWaHover(false)}
        >
          <div style={{
            background:'#161616', border:'1px solid rgba(200,169,110,0.2)', borderRight:'none',
            padding:'12px 18px', fontSize:11, letterSpacing:'0.1em', textTransform:'uppercase',
            color:'#C8A96E', whiteSpace:'nowrap',
            transform: waHover ? 'translateX(0)' : 'translateX(10px)',
            opacity: waHover ? 1 : 0,
            transition:'all .3s ease', pointerEvents:'none',
          }}>
            Falar agora
          </div>
          <a href={WA_LINK} target="_blank" rel="noreferrer" className="wa-btn" style={{
            display:'flex', alignItems:'center', justifyContent:'center',
            width:54, height:54,
            background: waHover ? '#128C7E' : '#25D366',
            textDecoration:'none', color:'white',
          }}>
            <WAIcon size={24}/>
          </a>
        </div>

      </div>
    </>
  );
}